import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import OnScroll from './OnScroll';
import SMHeaderContext from './SMHeaderContext';
import { Addon } from './components/Addon';
import UserDropDown from './components/UserDropDown';
import HeaderLogo from './components/HeaderLogo';
import { MobileDrawer } from './components/Drawer/Mobile';
import DesktopDrawer from './components/Drawer/Desktop';

import NavBarAnon from './anonymous/Desktop';
import NavBarAuth from './authenticated/Desktop';
import MobileAuth from './authenticated/Mobile';
import MobileAnon from './anonymous/Mobile';
import { ValidMenuNameList } from './config/linkInfo';
import { MobileTabletScreen, DesktopScreen } from '../..';

import './header.scss';
import './utils.scss';

const SMHeader = ({
  isUserAuthenticated,
  showCreateSurveyButton,
  color,
  lang,
  variant,
  isEUDC,
  actionFlow,
  user,
  isAnonHeaderSticky,
  isMRX,
  excludeMenuItems,
  showLoginButton,
  showSignupButton,
  alwaysUseAnonymousNav,
}) => {
  const auth = isUserAuthenticated ? 'auth' : 'anon';
  const headerColor = isUserAuthenticated ? 'green' : color;
  const headerVariant = isUserAuthenticated ? 'default' : variant;

  const navBarAnonVariant =
    !isUserAuthenticated && ['default', 'slim'].includes(variant);
  const navBarAuthVariant = isUserAuthenticated && variant === 'default';

  return (
    <SMHeaderContext.Provider
      value={{
        showCreateSurveyButton,
        isUserAuthenticated,
        lang,
        isEUDC,
        actionFlow,
        user,
        isMRX,
        excludeMenuItems,
      }}
    >
      <>
        <OnScroll
          render={(x, y) => {
            const headerColorAnon = y > 0 ? 'white' : color;
            const smHeaderColor = isUserAuthenticated
              ? headerColor
              : headerColorAnon;

            const defaultColor =
              headerColorAnon === 'transparent' ||
              headerColorAnon === 'primary';

            const logoColor = defaultColor ? 'alt' : 'primary';
            const mobileMenuColor = defaultColor ? 'alt' : 'secondary';
            const addonLogInColor = defaultColor ? 'alt' : 'secondary';
            const navBarColor = defaultColor ? 'alt' : 'secondary';
            const addonSignUpColor = defaultColor ? 'alt' : 'upgrade';
            const addonSignUpVariant = defaultColor ? 'ghost' : 'default';

            const classNames = classnames(
              `sm-header--${auth}`,
              `sm-header--${smHeaderColor}`,
              `sm-header--${headerVariant}`,
              { 'sm-header--en': lang === 'en' },
              {
                'sm-header--sticky': isAnonHeaderSticky && !isUserAuthenticated,
              }
            );

            return (
              <div className={classNames}>
                <MobileTabletScreen>
                  <Choose>
                    <When condition={isUserAuthenticated}>
                      <MobileDrawer
                        menuColor={navBarColor}
                        data-testid="Header__MobileDrawer--Auth"
                      >
                        <MobileAuth />
                      </MobileDrawer>
                    </When>
                    <Otherwise>
                      <MobileDrawer
                        menuColor={mobileMenuColor}
                        data-testid="Header__MobileDrawer--Anon"
                      >
                        <MobileAnon />
                      </MobileDrawer>
                    </Otherwise>
                  </Choose>
                </MobileTabletScreen>
                <HeaderLogo
                  variant={variant}
                  color={logoColor}
                  isUserAuthenticated={isUserAuthenticated}
                />
                {isUserAuthenticated && (
                  <div
                    style={{ order: 4 }}
                    className="sm-header--menu-drop-down--container"
                  >
                    <UserDropDown lang={lang} isEUDC={isEUDC} />
                  </div>
                )}
                <Choose>
                  <When
                    condition={
                      navBarAnonVariant || isMRX || alwaysUseAnonymousNav
                    }
                  >
                    <DesktopScreen>
                      <NavBarAnon color={navBarColor} lang={lang} />
                    </DesktopScreen>
                  </When>
                  <When
                    condition={
                      navBarAuthVariant && !isMRX && !alwaysUseAnonymousNav
                    }
                  >
                    <DesktopScreen>
                      <NavBarAuth user={user} />
                    </DesktopScreen>
                  </When>
                </Choose>
                <Addon
                  addonSignUpColor={addonSignUpColor}
                  addonLogInColor={addonLogInColor}
                  addonSignUpVariant={addonSignUpVariant}
                  variant={variant}
                  isUserAuthenticated={isUserAuthenticated}
                  showCreateSurveyButton={showCreateSurveyButton}
                  showLoginButton={showLoginButton}
                  showSignupButton={showSignupButton}
                />
                {isUserAuthenticated && (
                  <DesktopScreen>
                    <DesktopDrawer />
                  </DesktopScreen>
                )}
              </div>
            );
          }}
        />
      </>
    </SMHeaderContext.Provider>
  );
};

SMHeader.propTypes = {
  isAnonHeaderSticky: PropTypes.bool,
  color: PropTypes.oneOf(['primary', 'transparent', 'white', 'green']),
  isUserAuthenticated: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  showCreateSurveyButton: PropTypes.bool,
  lang: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['default', 'limited', 'slim']),
  isEUDC: PropTypes.bool.isRequired,
  actionFlow: PropTypes.string,
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isMRX: PropTypes.bool,
  excludeMenuItems: PropTypes.arrayOf(PropTypes.oneOf(ValidMenuNameList)),
  showLoginButton: PropTypes.bool,
  showSignupButton: PropTypes.bool,
  alwaysUseAnonymousNav: PropTypes.bool,
};

SMHeader.defaultProps = {
  isAnonHeaderSticky: true,
  color: 'primary',
  isUserAuthenticated: false,
  showCreateSurveyButton: true,
  variant: 'default',
  actionFlow: 'header',
  isMRX: false,
  excludeMenuItems: [],
  showLoginButton: true,
  showSignupButton: true,
  alwaysUseAnonymousNav: false,
};

export default SMHeader;
